import { Loader2, RefreshCw, Scale } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import type { Criterion, DecideResult, Decision } from "@/lib/types";
import { cn } from "@/lib/utils";
import { VERDICT_LABEL, VERDICT_VARIANT } from "@/lib/verdict";

/**
 * The "Should I apply?" scorecard: the verdict, the reasons, and every
 * criterion with its weight and what the posting said.
 *
 * The verdict is a fixed rule over the person's own priorities
 * (`core/decision.py`), not a model's opinion, so this panel shows its
 * working: a reader can check every line of how it was reached.
 */
export function DecisionView({ decision }: { decision: Decision }) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={VERDICT_VARIANT[decision.verdict]} className="text-sm">
          {VERDICT_LABEL[decision.verdict]}
        </Badge>
        {decision.score !== null ? (
          <span className="tabular text-sm text-muted-foreground">
            {decision.score} / 100
          </span>
        ) : null}
        <span className="text-xs text-muted-foreground">
          {Math.round(decision.known * 100)}% of what you care about is known
        </span>
      </div>
      <ul className="space-y-1 text-sm">
        {decision.reasons.map((reason) => (
          <li key={reason} className="flex gap-2">
            <span aria-hidden className="text-muted-foreground">
              –
            </span>
            {reason}
          </li>
        ))}
      </ul>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr>
              <th className="py-1 pr-3 font-medium">Criterion</th>
              <th className="py-1 pr-3 font-medium">Weight</th>
              <th className="py-1 pr-3 font-medium">Score</th>
              <th className="py-1 font-medium">What the posting says</th>
            </tr>
          </thead>
          <tbody>
            {decision.criteria.map((criterion) => (
              <CriterionRow key={criterion.key} criterion={criterion} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CriterionRow({ criterion }: { criterion: Criterion }) {
  const counted = criterion.applies;
  return (
    <tr
      className={cn(
        "border-t border-border/50 align-top",
        !counted && "text-muted-foreground",
      )}
    >
      <td className="py-1.5 pr-3">
        {criterion.label}
        {criterion.must ? (
          <Badge variant="outline" className="ml-1.5 px-1.5 py-0 text-[10px]">
            must
          </Badge>
        ) : null}
      </td>
      <td
        className="py-1.5 pr-3 font-mono text-xs"
        aria-label={`weight ${criterion.weight} of 3`}
      >
        {"●".repeat(criterion.weight)}
        <span className="text-muted-foreground/40">
          {"●".repeat(3 - criterion.weight)}
        </span>
      </td>
      <td className="py-1.5 pr-3">
        {!counted ? (
          <span className="text-xs">not counted</span>
        ) : criterion.score === null ? (
          <span className="text-xs text-muted-foreground">unknown</span>
        ) : (
          <ScoreBar value={criterion.score} />
        )}
      </td>
      <td className="py-1.5 text-xs">{criterion.detail}</td>
    </tr>
  );
}

function ScoreBar({ value }: { value: number }) {
  const percent = Math.round(value * 100);
  return (
    <div className="flex items-center gap-2">
      <div
        className="h-1.5 w-14 overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="criterion score"
      >
        <div
          className={cn(
            "h-full rounded-full",
            value >= 1
              ? "bg-success"
              : value > 0
                ? "bg-warning"
                : "bg-destructive",
          )}
          style={{ width: `${Math.max(percent, 4)}%` }}
        />
      </div>
      <span className="tabular text-xs">{percent}%</span>
    </div>
  );
}

/**
 * A button that asks "Should I apply?" for one posting and shows the answer.
 * The first time costs a model call or two (reading the facts, scoring the
 * fit); after that the answer comes from what was stored.
 */
export function DecideButton({
  jobId,
  initial,
  onDecided,
}: {
  jobId: string;
  initial?: Decision | null;
  onDecided?: (result: DecideResult) => void;
}) {
  const [decision, setDecision] = useState<Decision | null>(initial ?? null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(refresh: boolean) {
    setBusy(true);
    setError(null);
    try {
      const result = await api.applications.decide(jobId, refresh);
      setDecision(result.decision);
      setOpen(true);
      onDecided?.(result);
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          title="The first time reads the posting (a model call); after that it is free"
          onClick={() => (decision && !open ? setOpen(true) : void run(false))}
        >
          {busy ? (
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
          ) : (
            <Scale className="size-3.5" aria-hidden />
          )}
          {busy ? "Deciding…" : "Should I apply?"}
        </Button>
        {decision && !open ? (
          <Badge variant={VERDICT_VARIANT[decision.verdict]}>
            {VERDICT_LABEL[decision.verdict]}
          </Badge>
        ) : null}
        {open ? (
          <>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy}
              onClick={() => void run(true)}
              title="Read the posting again"
            >
              <RefreshCw className="size-3.5" aria-hidden />
              Re-read posting
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Hide
            </Button>
          </>
        ) : null}
      </div>
      {error ? (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {error}
        </p>
      ) : null}
      {open && decision ? (
        <div className="rounded-lg border bg-card/50 p-3">
          <DecisionView decision={decision} />
        </div>
      ) : null}
    </div>
  );
}
